import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Save, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "../../../../components/ui/button";
import { Skeleton } from "../../../../components/ui/skeleton";
import { cn } from "../../../../lib/utils";
import { ErrorBanner } from "../components/ErrorBanner";
import { useFees, useUpdateFee } from "../api/hooks";

// feeInt is tenths of a basis point; 100 = 0.1% (HL per-perp max).
const MAX_FEE_INT = 100;
const SCOPES = [
  { key: "perp", title: "Perp trading", blurb: "Fee on regular perp trades" },
  { key: "copy", title: "Copy trading", blurb: "Fee on copy-trades" },
];

export const toPercent = (feeInt) => feeInt / 1000;
export const toFeeInt = (percent) =>
  Math.min(MAX_FEE_INT, Math.max(0, Math.round(Number(percent) * 1000)));

export function FeesPage() {
  const fees = useFees();
  const queryClient = useQueryClient();

  const byKey = (fees.data ?? []).reduce((acc, f) => {
    acc[f.key] = f.feeInt;
    return acc;
  }, {});

  return (
    <div className="space-y-14 md:space-y-16">
      <header className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            <span className="h-px w-6 bg-ginox-green" />
            Hyperliquid
          </div>
          <h1 className="mt-3 break-words font-serif text-[40px] italic leading-[1.02] tracking-tightest text-foreground sm:text-[48px] xl:text-[56px]">
            Builder <span className="text-gradient-ginox">fees</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">
            The fee charged on every Hyperliquid fill. Max 0.1%. Changes take
            effect fleet-wide within ~30s.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => queryClient.invalidateQueries({ queryKey: ["hl", "fees"] })}
        >
          <RefreshCw className={cn("h-3.5 w-3.5", fees.isFetching && "animate-spin")} />
          Refresh
        </Button>
      </header>

      {fees.error ? (
        <ErrorBanner error={fees.error} onRetry={() => fees.refetch()} />
      ) : (
        <section className="grid gap-6 md:grid-cols-2">
          {SCOPES.map((scope) => (
            <FeeCard
              key={`${scope.key}-${byKey[scope.key] ?? "x"}`}
              scope={scope}
              feeInt={byKey[scope.key]}
              isLoading={fees.isLoading}
            />
          ))}
        </section>
      )}
    </div>
  );
}

function FeeCard({ scope, feeInt, isLoading }) {
  const update = useUpdateFee();
  // Seed from the server value; card remounts (via key) when feeInt changes.
  const [value, setValue] = useState(
    feeInt != null ? String(toPercent(feeInt)) : "",
  );

  const nextInt = value === "" ? null : toFeeInt(value);
  const dirty = nextInt != null && nextInt !== feeInt;
  const invalid = value !== "" && (Number.isNaN(Number(value)) || Number(value) < 0);

  const handleSave = async () => {
    if (!dirty || invalid) return;
    try {
      await update.mutateAsync({ key: scope.key, feeInt: nextInt });
      toast.success(`${scope.title} fee set to ${toPercent(nextInt)}%`);
    } catch (err) {
      toast.error(err?.message || "Failed to update fee");
    }
  };

  return (
    <div className="rounded-xl border border-border bg-white/[0.02] p-6">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-serif text-[22px] italic text-foreground">
          {scope.title}
        </h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          {scope.key}
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{scope.blurb}</p>

      <label className="mt-6 block text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        Fee percent
      </label>
      {isLoading ? (
        <Skeleton className="mt-2 h-11 w-full" />
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="number"
              step="0.001"
              min="0"
              max="0.1"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-full rounded-md border border-border bg-base-9/70 px-3 py-2.5 pr-8 font-mono text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-mono text-sm text-muted-foreground">
              %
            </span>
          </div>
          <Button
            variant="outline"
            size="md"
            disabled={!dirty || invalid || update.isPending}
            onClick={handleSave}
          >
            {update.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save
          </Button>
        </div>
      )}

      {!isLoading && (
        <p className="mt-2 font-mono text-[11px] text-muted-foreground">
          {invalid
            ? "Enter a percent between 0 and 0.1"
            : `feeInt ${nextInt ?? feeInt ?? 0} · max 0.1%`}
        </p>
      )}
    </div>
  );
}
